import configs from "./configs";
import { getAuthHeaders } from "./getToken";

const OPENAI_API = configs["OPENAI_API"];
export const OPENAI_AGENT_URL = `${OPENAI_API}/v1/ai`; // Export OPENAI_AGENT_URL
export const OPENAI_AUDIO_URL = `${OPENAI_API}/v1/audios`;
const TIMEOUTS = { text: 15000, audio: 30000 };

async function fetchWithTimeout(resource, options = {}, timeoutMs = TIMEOUTS.text) {
  const { signal: externalSignal, ...rest } = options || {};
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    try {
      controller.abort(new Error(`Request timed out`));
    } catch (err) {
      void err;
    }
  }, timeoutMs);

  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      try {
        controller.abort(externalSignal.reason);
      } catch (err) {
        void err;
      }
    } else {
      externalSignal.addEventListener(
        "abort",
        () => {
          try {
            controller.abort(externalSignal.reason);
          } catch (err) {
            void err;
          }
        },
        { once: true }
      );
    }
  }

  try {
    return await fetch(resource, { ...rest, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}
export const PROVIDERS = {
  OPEN_AI_ASSISTANT: "OpenAI Assistant",
  OPEN_AI_COMPLETION: "OpenAI Completion",
  AZURE_AI: "Azure AI",
  OPENAI_TRANSLATOR: "OpenAI Translator",
  OPENAI_TRANSCRIPTION: "OpenAI Transcription",
  OPENAI_SUMMARY: "OpenAI Summary"
};

export function getAiEntities() {
  return document.querySelectorAll("[npc-avatar]");
}

export function getAIEntity(aiThread) {
  let entity;
  document.querySelectorAll("[npc-avatar]").forEach(el => {
    if (el.components["npc-avatar"].npcId == aiThread) {
      entity = el;
    }
  });
  return entity;
}

export function getNpcComponent(aiEntity) {
  return aiEntity?.components["npc-avatar"] || aiEntity?.components["ai-agent"];
}

function randomString(len) {
  const p = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return [...Array(len)].reduce(a => a + p[~~(Math.random() * p.length)], "");
}

export async function createThread(apiKey) {
  /*
The response from the OpenAI API is a JSON object with the following structure:

- id: A unique identifier for the thread.
- object: The type of the object, in this case "thread".
- created_at: The timestamp of when the thread was created.
- metadata: An array of metadata associated with the thread.
- tool_resources: An array of tool resources associated with the thread.
*/
  const url = `${OPENAI_AGENT_URL}/thread`;
  const authHeaders = await getAuthHeaders(url, "POST");
  try {
    const response = await fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-AI-API-KEY": apiKey
        }
      },
      TIMEOUTS.text
    );

    const promptResponse = await response.json();
    return promptResponse?.id;
  } catch (e) {
    console.error(e);
    return false;
  }
}

export async function activateAgentPeer(payload) {
  const url = `${OPENAI_AGENT_URL}/agent/signaling-peer`;

  try {
    const response = await fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify(payload)
      },
      TIMEOUTS.text
    );

    if (!response.ok) {
      let errorPayload;
      try {
        errorPayload = await response.json();
      } catch (_) {
        errorPayload = await response.text();
      }
      throw new Error(
        `HTTP error ${response.status}: ${
          typeof errorPayload === "string" ? errorPayload : JSON.stringify(errorPayload)
        }`
      );
    }

    return await response.json();
  } catch (e) {
    console.error("Error activating agent peer", e);
    return null;
  }
}

export async function getPromptIntention(prompt /* Removed unused apiKey */) {
  /*
The response from the OpenAI API is a JSON object with the following structure:

- text: string with the response to the prompt.
*/
  const url = `${OPENAI_AGENT_URL}/prompt/intention`;
  const authHeaders = await getAuthHeaders(url, "POST");
  let promptResponse = "text";
  try {
    const response = await fetchWithTimeout(
      url,
      {
        method: "POST",
        body: JSON.stringify({ prompt }),
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
          Accept: "application/json"
        }
      },
      TIMEOUTS.text
    );

    promptResponse = await response.json();
    promptResponse.response = promptResponse.response === "image" ? promptResponse.response : "text";
  } catch (e) {
    console.error("Error sending thread prompt", e);
  }

  return promptResponse.response?.toLowerCase();
}

export function sendThreadPrompt({
  prompt,
  onMessageChunk,
  onEnd,
  onError,
  controller,
  onRetry,
  uuid,
  userId,
  persistable
}) {
  // The URL is now just the endpoint, parameters go into the body
  const url = `${OPENAI_AGENT_URL}/thread/text/completion`;

  const body = {
    uuid,
    prompt,
    userId
  };

  if (persistable) {
    body.persistableId = persistable;
  }

  if (!uuid) {
    if (onError) onError(new Error("UUID is required."));
    return controller; // or handle as appropriate
  }

  const maxRetries = 5;
  const retryDelay = 3000;
  let attempt = 0;
  let successReceived = false;

  // Track last activity for better timeout diagnostics
  let lastActivityAt = Date.now();
  let lastEventInfo = "init";

  // Per-attempt timeout management
  let attemptTimeoutId = null;
  // We need a reference to the current attempt's controller to abort it on timeout
  let currentAttemptController = null;

  const clearAttemptTimeout = () => {
    if (attemptTimeoutId) {
      clearTimeout(attemptTimeoutId);
      attemptTimeoutId = null;
    }
  };

  const armAttemptTimeout = () => {
    clearAttemptTimeout();
    attemptTimeoutId = setTimeout(() => {
      const now = Date.now();
      const idleMs = now - lastActivityAt;
      // Descriptive log to understand why timeout triggered
      console.error(
        `sendThreadPrompt timeout: no data within ${TIMEOUTS.text}ms. attempt=${attempt + 1}/${maxRetries}, ` +
          `aborted=${controller.signal.aborted}, successReceived=${successReceived}, uuid=${uuid}, ` +
          `idleForMs=${idleMs}, lastEvent=${lastEventInfo}`
      );
      if (!successReceived && !controller.signal.aborted) {
        // Do NOT abort the main controller, only the current attempt
        if (currentAttemptController) {
          try {
            currentAttemptController.abort(new Error("Timeout"));
          } catch (err) {
            void err;
          }
        }
      }
    }, TIMEOUTS.text);
  };

  const attemptSend = async () => {
    if (controller.signal.aborted) {
      console.log("Request aborted before sending.");
      clearAttemptTimeout();
      return;
    }

    const authHeaders = await getAuthHeaders(url, "POST");

    const headers = {
      ...authHeaders,
      "Content-Type": "application/json", // We are sending JSON in the body
      Accept: "text/event-stream" // We expect an event stream in response
    };

    // Create a controller specifically for this attempt
    currentAttemptController = new AbortController();
    const attemptSignal = currentAttemptController.signal;

    // Link main controller to attempt controller
    const onMainAbort = () => {
      currentAttemptController.abort(controller.signal.reason || new Error("Aborted by user"));
    };
    controller.signal.addEventListener("abort", onMainAbort, { once: true });

    try {
      armAttemptTimeout();
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal: attemptSignal // Use the attempt signal
      });

      if (!response.ok) {
        let errorPayload;
        try {
          errorPayload = await response.json(); // Try to parse as JSON
        } catch (e) {
          errorPayload = await response.text(); // Fallback to text
        }
        const httpError = new Error(
          `HTTP error ${response.status}: ${
            typeof errorPayload === "string" ? errorPayload : JSON.stringify(errorPayload)
          }`
        );
        httpError.status = response.status;
        httpError.response = response;
        httpError.payload = errorPayload;

        // Check for retryable HTTP errors if any, or just throw
        throw httpError;
      }

      if (!response.body) {
        throw new Error("Response body is null.");
      }

      // Decode manually to avoid environment-specific globals
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!attemptSignal.aborted) {
        if (attemptSignal.aborted) {
          break;
        }

        const { value, done } = await reader.read();

        if (done) {
          if (buffer.trim()) {
            // Process any remaining data in buffer
            console.warn("Stream ended with unprocessed data in buffer:", buffer);
          }
          if (onEnd) onEnd();
          clearAttemptTimeout();
          break;
        }

        // Decode chunk
        buffer += decoder.decode(value, { stream: true });
        lastActivityAt = Date.now();
        lastEventInfo = "chunk";
        // Reset timeout on activity
        armAttemptTimeout();

        let eolIndex;

        // SSE messages are separated by double newlines (\n\n)
        while ((eolIndex = buffer.indexOf("\n\n")) >= 0) {
          const message = buffer.substring(0, eolIndex);
          buffer = buffer.substring(eolIndex + 2); // Skip \n\n

          if (message.startsWith("data: ")) {
            const eventDataString = message.substring(6).trim(); // Remove "data: "
            if (eventDataString === "[DONE]") {
              // Example: Handle a special [DONE] message
              console.log("Received [DONE] marker from server.");
              // `done` from reader.read() should eventually also be true
              continue;
            }
            try {
              const data = JSON.parse(eventDataString);

              if (data.type === "error") {
                lastEventInfo = "error-event";
                const errorMessage = data.error || "Unknown error from stream";
                console.error("Error from OpenAI API (via stream):", errorMessage);

                // Construct error for retry logic
                throw new Error(typeof errorMessage === "string" ? errorMessage : JSON.stringify(errorMessage));
              } else {
                successReceived = true; // Mark success on first valid non-error message
                // clearAttemptTimeout(); // Don't clear, keep it armed for next chunk, just re-arm
                if (onMessageChunk) {
                  onMessageChunk(data);
                }
              }
            } catch (parseError) {
              console.error("Failed to parse SSE data:", eventDataString, parseError);
              // For parsing errors, we might want to retry if it looks transient, but often it's fatal.
              // Treating as fatal for now unless we want to look for "retryable"
              throw parseError;
            }
          } else if (message.startsWith("event: ")) {
            lastEventInfo = `event:${message.substring(7).trim()}`;
          }
        }
      }
    } catch (err) {
      if (controller.signal.aborted) {
        console.log(
          "Fetch operation was aborted by user/caller.",
          err.name === "AbortError" ? "(AbortError)" : `(${err.message})`
        );
        return;
      }

      const isTimeout =
        err.message === "Timeout" ||
        (attemptSignal.aborted && attemptSignal.reason && attemptSignal.reason.message === "Timeout");

      console.error("Fetch/Stream error:", err);
      // Check for specific retryable network or setup errors
      const errorMessage = err.message || "A network or setup error occurred";
      const status = typeof err.status === "number" ? err.status : err?.response?.status;

      let payloadMessage = "";
      if (err && err.payload) {
        if (typeof err.payload === "string") {
          payloadMessage = err.payload;
        } else if (typeof err.payload === "object") {
          try {
            payloadMessage = JSON.stringify(err.payload);
          } catch (_) {
            void _;
          }
        }
      }

      const lowerPayload = (payloadMessage || "").toLowerCase();
      const lowerErrorMsg = errorMessage.toLowerCase();
      const looksLikeServerProcessingError =
        lowerPayload.includes("server had an error processing your") ||
        lowerPayload.includes("retry your request") ||
        lowerErrorMsg.includes("server had an error processing your") ||
        lowerErrorMsg.includes("retry your request");

      const isRetryableCondition =
        !successReceived &&
        (isTimeout || // Timeout is now a retryable condition
          (typeof status === "number" && status >= 500 && status < 600) || // 5xx server errors
          looksLikeServerProcessingError);

      if (isRetryableCondition && attempt < maxRetries && !successReceived) {
        if (onRetry) onRetry();
        attempt++;
        console.warn(`Attempt ${attempt} failed with: "${errorMessage}". Retrying in ${retryDelay / 1000} seconds...`);
        clearAttemptTimeout();
        setTimeout(() => attemptSend(), retryDelay);
      } else {
        if (onError) onError(err);
        clearAttemptTimeout();
      }
    } finally {
      // Clean up listener
      controller.signal.removeEventListener("abort", onMainAbort);
    }
  };

  attemptSend();

  return controller;
}

export async function sendAudioPrompt(audio /* Removed unused apiKey */) {
  const formData = new FormData();
  formData.append("file", audio, `recording-${randomString(6)}.mp3`);
  const url = `${OPENAI_AGENT_URL}/audio/complete`;
  const authHeaders = await getAuthHeaders(url, "POST");
  /*
The response from the OpenAI API is a JSON object with the following structure:

- id: A unique identifier for the completion.
- object: The type of the object, in this case "chat.completion".
- created: The timestamp of when the completion was created.
- model: The model used to generate the completion.
- choices: An array of choices. Each choice has:
  - index: The index of the choice.
  - message: An object with the role ("assistant" in this case) and the content of the message.
  - logprobs: The log probabilities of the tokens in the message, if requested.
  - finish_reason: The reason the model stopped generating further tokens.
- usage: An object with the number of tokens used in the prompt, the completion, and total.
- system_fingerprint: A system fingerprint, if any.
*/
  let message;
  try {
    const response = await fetchWithTimeout(
      url,
      {
        method: "POST",
        body: formData,
        headers: authHeaders
      },
      TIMEOUTS.audio
    );

    const promptResponse = await response.json();
    message =
      promptResponse?.choices && promptResponse?.choices.length
        ? promptResponse?.choices[0]?.message?.content
        : "Sorry, I could not understand that.";
  } catch (e) {
    console.error(e);
    message = "Sorry, there was an error processing the audio";
  }

  return message;
}

// audio: Blob
export async function sendAudioTranscription({ audio, signal, uuid /* Removed apiKey */ }) {
  const formData = new FormData();
  formData.append("file", audio, `recording-${randomString(6)}.mp3`);
  formData.append("uuid", uuid);

  /*
The response from the OpenAI API is a JSON object with the following structure:
- text: string with audio transcribed.
*/
  const url = `${OPENAI_AGENT_URL}/audio/transcribe?uuid=${uuid}`;
  const authHeaders = await getAuthHeaders(url, "POST");
  const response = await fetchWithTimeout(
    url,
    {
      method: "POST",
      body: formData,
      headers: authHeaders,
      signal
    },
    TIMEOUTS.audio
  );

  const promptResponse = await response.json();
  return promptResponse?.text;
}

export async function getAudioSpeech(audio) {
  const response = await fetchWithTimeout(
    `${OPENAI_AUDIO_URL}/${audio}`,
    {
      method: "GET",
      body: JSON.stringify()
    },
    TIMEOUTS.audio
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const blob = new Blob([arrayBuffer], { type: "audio/wav" }); // Adjust the MIME type as needed
  const url = URL.createObjectURL(blob);

  return url;
}

export async function getAudioTTSOpenAI({ uuid, prompt, signal /* Removed apiKey */ }) {
  const url = `${OPENAI_AGENT_URL}/completion/audio`;
  const authHeaders = await getAuthHeaders(url, "POST");
  const response = await fetchWithTimeout(
    url,
    {
      method: "POST",
      body: JSON.stringify({ prompt, uuid }),
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      signal
    },
    TIMEOUTS.audio
  );

  if (!response.ok) {
    throw new Error(`TTS request failed with status ${response.status}`);
  }

  return response;
}

export async function translateText({ uuid, targetLanguage, texts }) {
  const url = `${OPENAI_AGENT_URL}/translate`;
  const authHeaders = await getAuthHeaders(url, "POST");
  try {
    const response = await fetchWithTimeout(
      url,
      {
        method: "POST",
        body: JSON.stringify({ uuid, targetLanguage, texts }),
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
          Accept: "application/json"
        }
      },
      TIMEOUTS.text
    );

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = null;
      }

      if (errorData?.type === "error") {
        return { error: { code: errorData.code, message: errorData.message, severity: errorData.severity } };
      }

      return {
        error: {
          code: "SERVICE_ERROR",
          message: "Translation service is temporarily unavailable",
          severity: "transient"
        }
      };
    }

    const data = await response.json();
    return { translations: data.translations };
  } catch (e) {
    console.error("Error translating text:", e);

    if (e?.message?.includes("timed out")) {
      return {
        error: {
          code: "TIMEOUT",
          message: "Translation request timed out. Please try again",
          severity: "transient"
        }
      };
    }

    return {
      error: {
        code: "SERVICE_ERROR",
        message: "Translation service is temporarily unavailable",
        severity: "transient"
      }
    };
  }
}
